import React, { useState, useEffect } from 'react';
import MuiBox from '@mui/material/Box';
import MuiTypography from '@mui/material/Typography';
import { Tooltip, Chip } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import SettingsIcon from '@mui/icons-material/Settings';
import MenuIcon from '@mui/icons-material/Menu';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PeopleIcon from '@mui/icons-material/People';
import PersonIcon from '@mui/icons-material/Person';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';

import { AppBar, StyledToolbar, LeftSection, RightSection, Title, HeaderIconButton } from './styles';
import ProfileMenu from '../profileMenu';
import Notifications from '../notifications';

// Componente de Notificações com segurança usando Error Boundary
const SafeNotifications: React.FC<{ onNotificationCountChange?: (count: number) => void }> = ({ onNotificationCountChange }) => {
  const [hasError, setHasError] = useState(false);

  if (hasError) {
    return null;
  }

  try {
    return (
      <React.Suspense fallback={null}>
        <Notifications onNotificationCountChange={onNotificationCountChange} />
      </React.Suspense>
    );
  } catch (error) {
    console.error('[SafeNotifications] Erro no render:', error);
    setHasError(true);
    return null;
  }
};

interface HeaderProps {
  title: string;
  showBackButton?: boolean;
  showMenuToggle?: boolean;
  menuOpen?: boolean;
  onMenuToggle?: () => void;
  hideActions?: boolean;
  actionButton?: React.ReactNode;
  subtitle?: string;
  isAdmin?: boolean;
  isMember?: boolean;
  isObserver?: boolean;
  showUserType?: boolean;
  isSystemAdmin?: boolean;
}

const Header: React.FC<HeaderProps> = ({
  title,
  showBackButton = true,
  showMenuToggle = false,
  menuOpen = false,
  onMenuToggle,
  hideActions = true,
  actionButton,
  subtitle,
  isAdmin = false,
  isMember = false,
  isObserver = false,
  showUserType = false,
  isSystemAdmin = false
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [notificationCount, setNotificationCount] = useState(0);


  const handleGoBack = () => {
    if (location.pathname.includes('/project/')) {
      navigate('/hub');
    } else {
      navigate(-1);
    }
  };

  return (
    <AppBar position="fixed" open={menuOpen}>
      <StyledToolbar>
        <LeftSection>
          {showMenuToggle && onMenuToggle && (
            <HeaderIconButton
              edge="start"
              onClick={onMenuToggle}
              aria-label="toggle menu"
            >
              {menuOpen ? <ChevronLeftIcon /> : <MenuIcon />}
            </HeaderIconButton>
          )}
          
          {showBackButton && (
            <HeaderIconButton onClick={handleGoBack} aria-label="go back">
              <ArrowBackIcon />
            </HeaderIconButton>
          )}
          
          <MuiBox sx={{ marginLeft: '16px' }}>
            <MuiBox sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Title variant="h6">{title}</Title>
              
              {showUserType && (
                <>
                  {isSystemAdmin && (
                    <Tooltip title="Você é administrador do sistema">
                      <Chip
                        icon={<PeopleIcon />}
                        label="Administrador do Sistema"
                        size="small"
                        sx={{
                          backgroundColor: '#6A1B9A',
                          color: 'white',
                          fontWeight: 500,
                          '& .MuiChip-icon': { color: 'white' }
                        }}
                      />
                    </Tooltip>
                  )}
                  {isAdmin && !isSystemAdmin && (
                    <Tooltip title="Você é administrador">
                      <Chip
                        icon={<PeopleIcon />}
                        label="Administrador"
                        size="small"
                        sx={{
                          backgroundColor: 'rgba(76, 175, 80, 0.12)',
                          color: '#43a047',
                          fontWeight: 500,
                          '& .MuiChip-icon': { color: '#43a047' }
                        }}
                      />
                    </Tooltip>
                  )}
                  
                  {isMember && !isAdmin && !isObserver && (
                    <Tooltip title="Você é membro">
                      <Chip
                        icon={<PersonIcon />}
                        label="Membro"
                        size="small"
                        sx={{
                          backgroundColor: 'rgba(255, 152, 0, 0.12)',
                          color: '#f57c00',
                          fontWeight: 500,
                          '& .MuiChip-icon': { color: '#f57c00' }
                        }}
                      />
                    </Tooltip>
                  )}
                  
                  {isObserver && (
                    <Tooltip title="Modo somente visualização">
                      <Chip
                        icon={<VisibilityIcon />}
                        label="Observador"
                        size="small"
                        sx={{
                          backgroundColor: 'rgba(25, 118, 210, 0.12)',
                          color: '#1976d2',
                          fontWeight: 500,
                          '& .MuiChip-icon': { color: '#1976d2' }
                        }}
                      />
                    </Tooltip>
                  )}
                </>
              )}
            </MuiBox>
            
            {subtitle && (
              <MuiTypography
                variant="body2"
                sx={{
                  color: 'rgba(255, 255, 255, 0.7)',
                  marginTop: '4px',
                  fontSize: '0.875rem'
                }}
              >
                {subtitle}
              </MuiTypography>
            )}
          </MuiBox>
        </LeftSection>
        
        <RightSection>
          {!hideActions && (
            <>
              <Tooltip title="Pesquisar">
                <HeaderIconButton aria-label="search">
                  <SearchIcon />
                </HeaderIconButton>
              </Tooltip>
              
              {/* Componente de notificações com segurança */}
              <SafeNotifications 
                onNotificationCountChange={setNotificationCount} 
              />
              
              <Tooltip title="Configurações">
                <HeaderIconButton aria-label="settings">
                  <SettingsIcon />
                </HeaderIconButton>
              </Tooltip>
            </>
          )}
          
          {/* Sempre mostrar notificações em desenvolvimento E debug em produção */}
          {hideActions && (
            <SafeNotifications 
              onNotificationCountChange={setNotificationCount} 
            />
          )}
          
          {actionButton && (
            <MuiBox sx={{ mr: 2 }}>
              {actionButton}
            </MuiBox>
          )}
          
          {/* Profile Menu Icon */}
          <ProfileMenu />
        </RightSection>
      </StyledToolbar>
    </AppBar>
  );
};

export default Header;
